import type { PaginationTableColumn } from '@/components/tables/pagination-table/types';
import { toProjectDisplayName } from '@/utils/projects/toProjectDisplayName';
import { fmtCurrency } from 'cesg-shared';
import { useI18n } from 'vue-i18n';
import type { AllInvoicesTableInvoice } from './types';

export const useAllInvoicesTableColumns =
  (): PaginationTableColumn<AllInvoicesTableInvoice>[] => {
    const { t } = useI18n();

    function fmtStatus(status: string): string {
      const statusMap: Record<string, string> = {
        incoming: t('invoiceStatus.incoming'),
        undercertificate: t('invoiceStatus.undercertificate'),
        paid: t('invoiceStatus.paid'),
        parked: t('invoiceStatus.parked'),
        disputed: t('invoiceStatus.disputed'),
        voided: t('invoiceStatus.voided'),
        approved: t('invoiceStatus.approved'),
        'verified-debt': t('invoiceStatus.notPaid'),
      };
      return statusMap[status] ?? status;
    }

    return [
      {
        key: 'serialNumber',
        label: t('adminSupplierInvoice.tableHeaders.serialNumber'),
        sortable: true,
        columnWidth: '70px',
        render: (row) => row.serialNumber,
        compareValue: (row) => Number(row.serialNumber),
      },
      {
        key: 'project',
        label: t('adminSupplierInvoice.tableHeaders.project'),
        sortable: true,
        render: (row) => toProjectDisplayName(row.project),
        columnWidth: '190px',
      },
      {
        key: 'accounts',
        label: t('adminSupplierInvoice.tableHeaders.accountType'),
        sortable: true,
        render: (row) =>
          row.accounts.map((a) => `${a.number} - ${a.name}`).join(', '),
        columnWidth: '200px',
      },
      {
        key: 'invoiceNumber',
        label: t('adminSupplierInvoice.tableHeaders.invoiceNumber'),
        sortable: true,
        columnWidth: '90px',
        render: (row) => row.invoiceNumber,
      },
      {
        key: 'status',
        label: t('adminSupplierInvoice.tableHeaders.status'),
        sortable: true,
        columnWidth: '90px',
        render: (row) => fmtStatus(row.status),
      },
      {
        key: 'supplier',
        label: t('adminSupplierInvoice.tableHeaders.supplier'),
        sortable: true,
        render: (row) => row.supplier,
        columnWidth: '180px',
      },
      {
        key: 'totalAmount',
        label: t('adminSupplierInvoice.tableHeaders.totalAmount'),
        sortable: true,
        columnWidth: '120px',
        render: (row) => fmtCurrency(row.totalAmount),
        compareValue: (row) => row.totalAmount,
      },
      {
        key: 'vat',
        label: t('adminSupplierInvoice.tableHeaders.vat'),
        sortable: true,
        columnWidth: '120px',
        render: (row) => fmtCurrency(row.vat),
        compareValue: (row) => row.vat,
      },
      {
        key: 'invoiceDate',
        label: t('adminSupplierInvoice.tableHeaders.invoiceDate'),
        sortable: true,
        columnWidth: '120px',
        render: (row) => row.invoiceDate,
        compareValue: (row) => new Date(row.invoiceDate).getTime(),
      },
      {
        key: 'dueDate',
        label: t('adminSupplierInvoice.tableHeaders.dueDate'),
        sortable: true,
        columnWidth: '120px',
        render: (row) => row.dueDate,
        compareValue: (row) => new Date(row.dueDate).getTime(),
      },
      {
        key: 'approver',
        label: t('adminSupplierInvoice.tableHeaders.approver'),
        sortable: true,
        columnWidth: '100px',
        render: (row) => row.approver,
      },
    ];
  };
